import { z } from 'zod'
import {
  displayNameSchema,
  emailSchema,
  ianaTimeZoneSchema,
  publicIdSchema,
  slugSchema,
  utcTimestampSchema,
} from './primitives'

/**
 * Competition Creation Request (Wave O). GOV-019/020, PR-025..027,
 * DM-019..021, DATA-026/027. See
 * docs/sdd/08-platform-governance-workflows.md's "Competition Creation
 * Request" section for the full state machine.
 */
export const COMPETITION_REQUEST_STATUS_VALUES = [
  'draft',
  'submitted',
  'under_review',
  'changes_requested',
  'approved',
  'rejected',
  'cancelled',
] as const
export const competitionRequestStatusSchema = z.enum(COMPETITION_REQUEST_STATUS_VALUES)
export type CompetitionRequestStatus = z.infer<typeof competitionRequestStatusSchema>

/** States from which a request can still move toward Approve/Reject. */
export const COMPETITION_REQUEST_APPROVABLE_STATUSES: readonly CompetitionRequestStatus[] = [
  'submitted',
  'under_review',
  'changes_requested',
]

/** States from which the applicant may still cancel their own request. */
export const COMPETITION_REQUEST_CANCELLABLE_STATUSES: readonly CompetitionRequestStatus[] = [
  'submitted',
  'under_review',
  'changes_requested',
]

export const competitionCreationRequestSchema = z.object({
  id: publicIdSchema,
  applicantUserId: publicIdSchema,
  proposedName: z.string(),
  proposedSlug: z.string(),
  organization: z.string(),
  contactPerson: z.string(),
  contactEmail: emailSchema,
  description: z.string(),
  timezone: z.string(),
  expectedStartAt: utcTimestampSchema.nullable(),
  expectedEndAt: utcTimestampSchema.nullable(),
  expectedParticipants: z.number().int().nullable(),
  judgingRequired: z.boolean(),
  twoRoundRequired: z.boolean(),
  studentMinorData: z.boolean(),
  notes: z.string().nullable(),
  status: competitionRequestStatusSchema,
  reviewerUserId: publicIdSchema.nullable(),
  reviewReason: z.string().nullable(),
  submittedAt: utcTimestampSchema,
  reviewedAt: utcTimestampSchema.nullable(),
  createdCompetitionId: publicIdSchema.nullable(),
  /**
   * v4.46 Competition Configuration JSON-only, governance revision
   * (`docs/sdd/08-platform-governance-workflows.md` section 8.1): non-null
   * only for a request submitted via Clone / Create from Template / Import
   * Create - a fully validated, already date-transformed
   * `CompetitionConfigurationSnapshot`, JSON-serialized. When present,
   * Automated Provisioning builds the approved Competition from this
   * snapshot instead of the plain identity fields above.
   */
  configurationSnapshotJson: z.string().nullable(),
  configurationYear: z.int().min(2000).max(2999).nullable(),
  configurationSourceKind: z.enum(['clone', 'template', 'import']).nullable(),
  /** Source competitionId (clone) or templateId (template); null for import or the plain applicant flow. */
  configurationSourceRef: publicIdSchema.nullable(),
  createdAt: utcTimestampSchema,
  updatedAt: utcTimestampSchema,
})
export type CompetitionCreationRequest = z.infer<typeof competitionCreationRequestSchema>

/** UX-016/017: what an applicant/reviewer is allowed to see. Same shape for both audiences. */
export const competitionCreationRequestDtoSchema = competitionCreationRequestSchema
export type CompetitionCreationRequestDto = z.infer<typeof competitionCreationRequestDtoSchema>

/**
 * Applicant submission (PR-025/BR-072). SEC-014: bounded field sizes.
 * The "minimum suggested" fields from the governance doc's §3 are required;
 * the rest are optional context a reviewer uses to judge scale/risk. Which
 * of these are ultimately mandatory is a Product Owner decision not yet
 * made (see Wave O delivery log) — this is a deliberately conservative
 * placeholder set.
 */
export const submitCompetitionRequestSchema = z.strictObject({
  proposedName: z.string().trim().min(1).max(200),
  proposedSlug: slugSchema,
  organization: displayNameSchema,
  contactPerson: displayNameSchema,
  contactEmail: emailSchema,
  description: z.string().trim().min(1).max(4_000),
  timezone: ianaTimeZoneSchema,
  expectedStartAt: utcTimestampSchema.nullable().default(null),
  expectedEndAt: utcTimestampSchema.nullable().default(null),
  expectedParticipants: z.number().int().min(0).max(1_000_000).nullable().default(null),
  judgingRequired: z.boolean().default(true),
  twoRoundRequired: z.boolean().default(false),
  studentMinorData: z.boolean().default(false),
  notes: z.string().trim().max(2_000).nullable().default(null),
})
export type SubmitCompetitionRequest = z.infer<typeof submitCompetitionRequestSchema>

/**
 * Platform review decision (PR-026/API-042). Reason is required for every
 * decision per the governance doc §5 ("Reject / Request Changes / Approve
 * 應保存 reason") — UX-018's "核准並建立競賽" wording is enforced client-side
 * only; the server enforces the non-empty reason.
 */
export const reviewCompetitionRequestSchema = z.strictObject({
  reason: z.string().trim().min(1).max(2_000),
})
export type ReviewCompetitionRequest = z.infer<typeof reviewCompetitionRequestSchema>

export const competitionRequestListQuerySchema = z.object({
  status: competitionRequestStatusSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).default(0),
})
export type CompetitionRequestListQuery = z.infer<typeof competitionRequestListQuerySchema>

/**
 * AC-033/API-042 approve response: the request (now `approved`) plus the
 * competition it provisioned — enough for the caller to navigate straight
 * to the new Competition without a manual follow-up step (UX-018).
 */
export const competitionRequestApprovalResultSchema = z.object({
  request: competitionCreationRequestDtoSchema,
  competitionId: publicIdSchema,
  /** True when this call found the request already provisioned and returned the existing result rather than creating anything (AC-034/BR-074). */
  alreadyProvisioned: z.boolean(),
})
export type CompetitionRequestApprovalResult = z.infer<
  typeof competitionRequestApprovalResultSchema
>
